import { useCustom } from "@refinedev/core";
import type { components } from "@libertify/api-types/analytics-api";

export type OrganizationSessionTotals =
  components["schemas"]["OrganizationSessionTotals"];
export type ProjectSessionSummary =
  components["schemas"]["ProjectSessionSummary"];
type ProjectSummariesPaginated =
  components["schemas"]["PaginatedResponse_ProjectSessionSummary_"];

const STALE_TIME = 1000 * 60 * 5;

export function useOrganizationTotals(organizationId: string | undefined): {
  totals: OrganizationSessionTotals | undefined;
  isLoading: boolean;
} {
  const { data, isLoading, isFetching } = useCustom<OrganizationSessionTotals>({
    url: `analytics/organizations/${organizationId}/totals`,
    method: "get",
    queryOptions: {
      enabled: Boolean(organizationId),
      staleTime: STALE_TIME,
      retry: false,
    },
    errorNotification: false,
  });

  return {
    totals: data?.data,
    isLoading: isLoading || isFetching,
  };
}

export type UseOrganizationSummariesOptions = {
  page?: number;
  /** Page size (BE caps at 50). */
  size?: number;
  /** Restrict the summaries to these project ids; null/undefined = all. */
  projectIds?: string[] | null;
  /** Set false to hold the request (e.g. until the project ids are known). */
  enabled?: boolean;
};

export function useOrganizationSummaries(
  organizationId: string | undefined,
  options: UseOrganizationSummariesOptions = {}
): {
  summaries: ProjectSessionSummary[] | undefined;
  totalResults: number | undefined;
  isLoading: boolean;
} {
  const { page, size, projectIds, enabled = true } = options;

  // page/size go in the URL itself: the data provider drops config.query on
  // POST requests, so the API would otherwise fall back to size=10.
  const params = new URLSearchParams();
  if (page != null) params.set("page", String(page));
  if (size != null) params.set("size", String(size));
  const queryString = params.toString();

  const { data, isLoading, isFetching } = useCustom<ProjectSummariesPaginated>({
    url: `analytics/organizations/${organizationId}/summaries${
      queryString ? `?${queryString}` : ""
    }`,
    method: "post",
    config: {
      // project_ids goes in the request body.
      payload: { project_ids: projectIds ?? null },
    },
    queryOptions: {
      enabled: Boolean(organizationId) && enabled,
      staleTime: STALE_TIME,
      retry: false,
    },
    errorNotification: false,
  });

  return {
    summaries: data?.data?.items,
    totalResults: data?.data?.pagination?.total_results,
    // A held query stays isLoading=true in React Query v4; it is not loading.
    isLoading: enabled && (isLoading || isFetching),
  };
}
